import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";

/**
 * Cognito is the only identity provider. This wraps oidc-client-ts against the
 * pool's OIDC issuer using Authorization Code + PKCE via the Hosted UI.
 */
export type AuthContextValue = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Start the Hosted UI sign-in. `returnTo` is restored after the callback. */
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Completes the redirect. Resolves to the path the user should land on. */
  /** The path to continue at, or null when the response was a silent renewal handed to the opening page and there is nothing to show. */
  handleCallback: () => Promise<string | null>;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

type CallbackState = { returnTo?: string };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const env = import.meta.env;

/**
 * Local development only: when VITE_DEV_BYPASS_AUTH=true under `vite dev`,
 * skip Cognito and act as a fake signed-in user. The API must run with
 * DEV_BYPASS_AUTH=true too. Vite production builds never include this branch.
 */
const devBypass = env.DEV && env.VITE_DEV_BYPASS_AUTH === "true";

const createDevUser = (): User =>
  ({
    id_token: "dev-bypass",
    access_token: "dev-bypass",
    token_type: "Bearer",
    scope: "openid email profile",
    expired: false,
    expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    profile: {
      sub: "dev-user",
      iss: "dev",
      aud: "dev",
      exp: 0,
      iat: 0,
      email: "dev@localhost",
      name: "Dev Admin",
      "cognito:groups": ["bkdp-admin"]
    },
    state: undefined
  }) as unknown as User;

const createUserManager = () => {
  if (devBypass) {
    return null;
  }
  const authorityEnv = env.VITE_COGNITO_AUTHORITY;
  const region = env.VITE_COGNITO_REGION;
  const userPoolId = env.VITE_COGNITO_USER_POOL_ID;
  const clientId = env.VITE_COGNITO_CLIENT_ID;
  const redirectUri = env.VITE_COGNITO_REDIRECT_URI ?? `${window.location.origin}/auth/callback`;
  const logoutUri = env.VITE_COGNITO_LOGOUT_URI ?? `${window.location.origin}/`;
  const scope = env.VITE_COGNITO_SCOPE ?? "openid email profile";

  const authority =
    authorityEnv ??
    (region && userPoolId
      ? `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`
      : undefined);

  if (!authority || !clientId) {
    console.warn("Auth config missing", { authority, clientId, redirectUri, logoutUri });
    return null;
  }

  return new UserManager({
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: logoutUri,
    response_type: "code",
    scope,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: RENEW_AHEAD_SECONDS,
    silent_redirect_uri: redirectUri,
    userStore: new WebStorageStateStore({ store: window.localStorage })
  });
};

/**
 * Renewal retry (1.9, the policy). A silent renewal that fails is retried while the session is still valid, with the
 * waits below between tries; the person is sent to sign in again only when the access token has really expired, or
 * when the refresh token itself was refused (invalid_grant: revoked or past its life), which no retry can mend. The
 * library retries only a timeout by itself: a network failure or a 5xx from Cognito raised one error and stopped, and
 * the provider used to force a fresh sign-in on that first error, mid-session, with minutes of a valid token left.
 * The first failure's reason is logged once per run of failures, and so is how the run ended. The renewal starts
 * RENEW_AHEAD_SECONDS before expiry so the retries have room.
 */
export const RENEW_RETRY_WAITS_MS = [5_000, 10_000, 20_000, 40_000, 60_000] as const;
export const RENEW_AHEAD_SECONDS = 300;
/** The wait before the n-th retry (n from 1); the last wait repeats. */
export const renewRetryWait = (attempt: number): number => RENEW_RETRY_WAITS_MS[Math.min(Math.max(attempt, 1), RENEW_RETRY_WAITS_MS.length) - 1];
/** True when the refresh token itself was refused, so no retry can succeed. The library marks its ErrorResponse by name. */
export const renewalIsFinal = (err: unknown): boolean => {
  const e = err as { name?: string; error?: string | null } | null;
  return Boolean(e && e.name === "ErrorResponse" && e.error === "invalid_grant");
};
const reasonOf = (err: unknown): string => (err instanceof Error ? err.message || err.name : String(err));

const safeReturnTo = (value: unknown): string => {
  if (typeof value === "string" && value.startsWith("/") && !value.startsWith("//")) {
    return value;
  }
  return "/";
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(() => (devBypass ? createDevUser() : null));
  const [isLoading, setIsLoading] = useState(!devBypass);
  const [manager] = useState(() => createUserManager());

  const redirectToLogin = useCallback(async () => {
    if (!manager) return;
    setUser(null);
    await manager.removeUser();
    const state: CallbackState = { returnTo: window.location.pathname + window.location.search };
    await manager.signinRedirect({ state });
  }, [manager]);

  useEffect(() => {
    if (!manager) {
      if (!devBypass) setIsLoading(false);
      return;
    }

    let isMounted = true;
    manager.getUser().then((storedUser) => {
      if (!isMounted) return;
      if (storedUser?.expired) {
        void redirectToLogin();
        return;
      }
      setUser(storedUser ?? null);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [manager, redirectToLogin]);

  useEffect(() => {
    if (!manager) return;

    // The renewal retry policy (see RENEW_RETRY_WAITS_MS): failures counts the current run, retry is the pending try.
    let failures = 0;
    let retry: number | null = null;
    const clearRetry = () => {
      if (retry !== null) window.clearTimeout(retry);
      retry = null;
    };
    const signInAgain = (why: string | null) => {
      clearRetry();
      if (why) console.warn(`Signing in again: ${why}.`);
      failures = 0;
      void redirectToLogin();
    };
    const handleExpired = () => {
      signInAgain(failures > 0 ? `the session expired after ${failures} failed renewal${failures === 1 ? "" : "s"}` : null);
    };
    const handleRenewError = (err: unknown) => {
      failures += 1;
      if (failures === 1) console.warn(`Sign-in renewal failed: ${reasonOf(err)}. Retrying while the session is still valid.`);
      if (renewalIsFinal(err)) {
        signInAgain("the refresh token was refused (invalid_grant), which no retry can mend");
        return;
      }
      void manager.getUser().then((current) => {
        if (!current || current.expired) {
          handleExpired();
          return;
        }
        clearRetry();
        retry = window.setTimeout(() => {
          retry = null;
          // A retry's own failure comes back here; the library raises its event only for the renewals it starts.
          manager.signinSilent().then(() => undefined, handleRenewError);
        }, renewRetryWait(failures));
      });
    };
    const handleRenewed = (renewed: User) => {
      if (failures > 0) console.warn(`Sign-in renewal succeeded after ${failures} failure${failures === 1 ? "" : "s"}.`);
      failures = 0;
      clearRetry();
      setUser(renewed);
    };

    manager.events.addAccessTokenExpired(handleExpired);
    manager.events.addSilentRenewError(handleRenewError);
    manager.events.addUserSignedOut(handleExpired);
    manager.events.addUserLoaded(handleRenewed);

    return () => {
      clearRetry();
      manager.events.removeAccessTokenExpired(handleExpired);
      manager.events.removeSilentRenewError(handleRenewError);
      manager.events.removeUserSignedOut(handleExpired);
      manager.events.removeUserLoaded(handleRenewed);
    };
  }, [manager, redirectToLogin]);

  const login = useCallback(
    async (returnTo?: string) => {
      if (!manager) return;
      const state: CallbackState = {
        returnTo: returnTo ?? window.location.pathname + window.location.search
      };
      await manager.signinRedirect({ state });
    },
    [manager]
  );

  const logout = useCallback(async () => {
    if (!manager) return;
    setUser(null);
    await manager.removeUser();
    const logoutUri = env.VITE_COGNITO_LOGOUT_URI ?? `${window.location.origin}/`;
    const domain = env.VITE_COGNITO_DOMAIN;
    const clientId = env.VITE_COGNITO_CLIENT_ID;
    if (domain && clientId) {
      // End the Hosted UI session too, otherwise "Sign in" silently signs back in.
      const query = new URLSearchParams({ client_id: clientId, logout_uri: logoutUri });
      window.location.assign(`https://${domain}/logout?${query.toString()}`);
      return;
    }
    window.location.assign(logoutUri);
  }, [manager]);

  /**
   * Completes a sign-in response. The same address serves two flows: a person coming back from the Hosted UI at the
   * top of the window, and a silent renewal answered inside a hidden frame (silent_redirect_uri is this route). In the
   * frame the response belongs to the page that opened the frame: it is handed back to that page and nothing else
   * happens here, so the shell never boots, navigates or signs anyone in from inside a frame. Before this the frame
   * processed the response as a top-level sign-in, never told its parent, and the parent timed out, raised a silent-
   * renew error, and forced a fresh login mid-session. Returns null when there is nothing for the caller to render.
   */
  const handleCallback = useCallback(async (): Promise<string | null> => {
    if (!manager) return "/";
    if (window.self !== window.top) {
      await manager.signinSilentCallback();
      return null;
    }
    const signedInUser = await manager.signinRedirectCallback();
    setUser(signedInUser);
    const state = (signedInUser.state ?? {}) as CallbackState;
    return safeReturnTo(state.returnTo);
  }, [manager]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user?.id_token && !user?.expired),
      login,
      logout,
      handleCallback
    }),
    [user, isLoading, login, logout, handleCallback]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
